import type { TearsheetReportStatus } from '@hf/sharedContract';

/**
 * GA4 catalog. `event` is the GA event name (snake_case, ≤40 chars).
 * `action` is the human-readable label sent on every hit for leadership reports.
 */
export const GA = {
  signedIn: { event: 'signed_in', action: 'Signed in' },
  signInFailed: { event: 'sign_in_failed', action: 'Sign-in failed' },
  signInNeedsCode: { event: 'sign_in_needs_code', action: 'Sign-in requested email code' },
  verifiedSignInCode: { event: 'verified_sign_in_code', action: 'Verified sign-in code' },
  resentSignInCode: { event: 'resent_sign_in_code', action: 'Resent sign-in code' },
  startedPasswordSetup: { event: 'started_password_setup', action: 'Started password setup' },
  finishedPasswordSetup: { event: 'finished_password_setup', action: 'Finished password setup' },
  signedOut: { event: 'signed_out', action: 'Signed out' },
  sessionExpired: { event: 'session_expired', action: 'Session expired' },
  clicked: { event: 'click', action: 'Clicked' },
  openedWorkspace: { event: 'opened_workspace', action: 'Opened workspace' },
  viewedTearsheet: { event: 'viewed_tearsheet', action: 'Viewed tearsheet' },
  startedTearsheet: { event: 'started_tearsheet', action: 'Started new tearsheet' },
  savedTearsheetDraft: { event: 'saved_tearsheet_draft', action: 'Saved tearsheet draft' },
  submittedTearsheet: { event: 'submitted_tearsheet', action: 'Submitted tearsheet' },
  approvedTearsheet: { event: 'approved_tearsheet', action: 'Approved tearsheet' },
  publishedTearsheet: { event: 'published_tearsheet', action: 'Published tearsheet' },
  rejectedTearsheet: { event: 'rejected_tearsheet', action: 'Rejected tearsheet' },
  unpublishedTearsheet: { event: 'unpublished_tearsheet', action: 'Unpublished tearsheet' },
  reopenedTearsheet: { event: 'reopened_tearsheet', action: 'Re-opened tearsheet' },
  exportedTearsheetPdf: { event: 'exported_tearsheet_pdf', action: 'Exported tearsheet PDF' },
  regeneratedTearsheetPdf: { event: 'regenerated_tearsheet_pdf', action: 'Regenerated tearsheet PDF' },
  copiedTearsheetLink: { event: 'copied_tearsheet_link', action: 'Copied tearsheet link' },
  savedTearsheetLayout: { event: 'saved_tearsheet_layout', action: 'Saved tearsheet layout' },
  duplicatedTearsheetLayout: { event: 'duplicated_tearsheet_layout', action: 'Duplicated tearsheet layout' },
  deletedTearsheetLayout: { event: 'deleted_tearsheet_layout', action: 'Deleted tearsheet layout' },
  savedLayoutFavorites: { event: 'saved_layout_favorites', action: 'Saved layout favorites' },
  savedOmsColumnLayout: { event: 'saved_oms_column_layout', action: 'Saved OMS column layout' },
  savedOmsCustomLayout: { event: 'saved_oms_custom_layout', action: 'Saved OMS custom layout' },
  deletedOmsCustomLayout: { event: 'deleted_oms_custom_layout', action: 'Deleted OMS custom layout' },
  exportedOmsGridCsv: { event: 'exported_oms_grid_csv', action: 'Exported OMS grid CSV' },
  addedTearsheetSection: { event: 'added_tearsheet_section', action: 'Added tearsheet stencil' },
  editedTearsheetSection: { event: 'edited_tearsheet_section', action: 'Edited tearsheet stencil' },
  savedTearsheetSkin: { event: 'saved_tearsheet_skin', action: 'Saved tearsheet skin' },
  savedTearsheetBranding: { event: 'saved_tearsheet_branding', action: 'Saved tearsheet branding' },
  savedProfile: { event: 'saved_profile', action: 'Saved profile' },
  changedPassword: { event: 'changed_password', action: 'Changed password' },
  changedTheme: { event: 'changed_theme', action: 'Changed theme' },
  createdCompanyUser: { event: 'created_company_user', action: 'Created company user' },
  deactivatedCompanyUser: { event: 'deactivated_company_user', action: 'Deactivated company user' },
  restoredCompanyUser: { event: 'restored_company_user', action: 'Restored company user' },
  changedCompanyUserRole: { event: 'changed_company_user_role', action: 'Changed company user role' },
  createdFeatureFlag: { event: 'created_feature_flag', action: 'Created feature flag' },
  updatedFeatureFlag: { event: 'updated_feature_flag', action: 'Updated feature flag' },
  deletedFeatureFlag: { event: 'deleted_feature_flag', action: 'Deleted feature flag' },
  resentPasswordSetup: { event: 'resent_password_setup', action: 'Resent password setup code' },
  verifiedPasswordSetup: { event: 'verified_password_setup', action: 'Verified password setup code' },
  acceptedAnalytics: { event: 'accepted_analytics', action: 'Accepted analytics cookies' },
} as const;

export type GaKey = keyof typeof GA;
export type GaAction = (typeof GA)[GaKey];

export type GaParams = {
  item_id?: string;
  as_of?: string;
  layout?: string;
  format?: string;
  role?: string;
  section?: string;
};

export function catalogByEvent(event: string): GaAction | undefined {
  return Object.values(GA).find((item) => item.event === event);
}

export function tearsheetStatusAction(next: TearsheetReportStatus, previous?: string): GaAction {
  if (next === 'submitted') return GA.submittedTearsheet;
  if (next === 'approved') return GA.approvedTearsheet;
  if (next === 'published') return GA.publishedTearsheet;
  if (next === 'archived') return GA.rejectedTearsheet;
  if (next === 'draft') return previous === 'published' ? GA.unpublishedTearsheet : GA.reopenedTearsheet;
  return GA.savedTearsheetDraft;
}

export function pdfFormatLabel(mode: string): string {
  return mode === 'a4' ? 'Paginated A4' : 'Single continuous page (Letter)';
}
